import React, { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  Warehouse,
  WarehouseListArray,
} from "../../../@types/DRO/GetAllWarehouseResponse";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";

interface AppTransferOrderFormProps {
  initalData?: {
    sourceWarehouse: Warehouse;
    destinationWarehouse: Warehouse;
    allParts: TransferOrderPartDetails[];
  };
  isDraft?: boolean;
  deleteEnabled?: boolean;
  addEnabled?: boolean;
  onSubmit?: (newDetails: {
    sourceWarehouse: Warehouse;
    destinationWarehouse: Warehouse;
    allParts: TransferOrderPartDetails[];
    status: "CREATED" | "DRAFT";
  }) => void;
}
export const AppTransferOrderForm: React.FC<AppTransferOrderFormProps> = ({
  deleteEnabled = false,
  addEnabled = false,
  onSubmit = () => {},
  isDraft = false,
  initalData = null,
}) => {
  const initalWarehouseOptions: Warehouse[] = [
    initalData?.sourceWarehouse as Warehouse,
    initalData?.destinationWarehouse as Warehouse,
  ].filter((e) => !!e);
  const [currentlyAddingPartDetails, setCurrentAddingPartDetails] =
    useState<TransferOrderPartDetails | null>(null);
  const [allAvailableWarehouses, setAllAvailableWarehouses] = useState<
    WarehouseListArray["data"]
  >(initalWarehouseOptions);
  const [sourceWarehouse, setSourceWarehouse] = useState<Warehouse | null>(
    initalData?.sourceWarehouse || null
  );
  const [destinationWarehouse, setDestinationWarehouse] =
    useState<Warehouse | null>(initalData?.destinationWarehouse || null);
  const [parts, setParts] = useState<TransferOrderPartDetails[]>(
    initalData?.allParts || []
  );

  const [warehouseCurrentPageNumber, setWarehouseCurrentPageNumber] =
    useState(1);
  const [partsCurrentPageNumber, setPartsCurrentPageNumber] = useState(1);
  const [totalWithoutTaxes, setTotalAmountWithoutTaxes] = useState<string>("");
  const [totalGST, setTotalGST] = useState<string>("");
  const [totalWithTaxes, setTotalWithTaxes] = useState<string>("");
  const [items, setItems] = useState<TransferOrderPartDetails[]>(
    initalData?.allParts || []
  );
  function handleSubmit(status: "CREATED" | "DRAFT") {
    if (!sourceWarehouse || !destinationWarehouse) {
      return;
    }
    onSubmit({
      allParts: items,
      sourceWarehouse,
      destinationWarehouse,
      status,
    });
  }
  const fetchNextParts = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getPartList(
        partsCurrentPageNumber,
        30,
        ""
      );
      setParts((prevParts) =>
        [...prevParts, ...response.data.data].filter(
          (p, i, a) => a.findIndex((t) => t.id === p.id) === i
        )
      );
    } catch (error) {
      console.error("Error fetching parts", error);
    }
  }, [partsCurrentPageNumber]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        warehouseCurrentPageNumber,
        20,
        ""
      );
      setAllAvailableWarehouses((prev) => {
        const set = new Set<number>();
        return [...prev, ...response.data.data].filter(
          (response: Warehouse) => !set.has(response.id) && set.add(response.id)
        );
      });
    } catch (error) {
      console.error("Error fetching warehouses", error);
    }
  }, [warehouseCurrentPageNumber]);
  function isAtBotton(event: React.SyntheticEvent, offset = 0) {
    return (
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight - offset
    );
  }
  const handleScroll = (event: React.SyntheticEvent) => {
    if (isAtBotton(event)) {
      setWarehouseCurrentPageNumber((prevPage) => prevPage + 1);
    }
  };
  function getTotalPriceOfPart(
    quantity: number,
    price: number,
    taxRate: number
  ) {
    return parseFloat(((quantity * price * (100 + taxRate)) / 100).toFixed(2));
  }
  useEffect(() => {
    fetchNextParts();
  }, [partsCurrentPageNumber, fetchNextParts]);

  useEffect(() => {
    fetchWarehouses();
  }, [warehouseCurrentPageNumber, fetchWarehouses]);
  useEffect(() => {
    const intialQuantity = currentlyAddingPartDetails?.quantity || 0;
    const intialPrice = currentlyAddingPartDetails?.price || 0;
    const intialTaxed = currentlyAddingPartDetails?.quantity || 0;
    let totalAmount = intialQuantity * intialPrice;
    let totalGST = (intialTaxed / 100) * totalAmount;
    items.forEach((item) => {
      const quantity = item.quantity || 0;
      const price = item.price;
      const taxRate = item.tax / 100;

      const itemTotal = quantity * price;
      const itemGST = itemTotal * taxRate;

      totalAmount += itemTotal;
      totalGST += itemGST;
    });
    setTotalAmountWithoutTaxes(totalAmount.toFixed(2));
    setTotalGST(totalGST.toFixed(2));
    setTotalWithTaxes((totalGST + totalAmount).toFixed(2));
  }, [items, currentlyAddingPartDetails]);

  return (
    <div className="w-full p-4">
      <Paper
        sx={{
          p: 4,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          backgroundColor: "white",
          maxWidth: "1600px",
          margin: "0 auto",
        }}>
        {/* Form Section */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3}>
            {/* Source Location */}
            <Grid item xs={12} md={4}>
              <Box>
                <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                  Source Location
                </label>
                <Autocomplete
                  fullWidth
                  disabled={isDraft}
                  options={allAvailableWarehouses}
                  getOptionLabel={(option) => option.name}
                  value={sourceWarehouse}
                  onChange={(_, newValue) => {
                    setSourceWarehouse(newValue);
                    setDestinationWarehouse(null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: "0.875rem" },
                      }}
                    />
                  )}
                  ListboxProps={{
                    onScroll: (event) => {
                      if (isAtBotton(event)) {
                        setWarehouseCurrentPageNumber(
                          (prevPage) => prevPage + 1
                        );
                      }
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Deliver To */}
            <Grid item xs={12} md={4}>
              <Box>
                <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                  Deliver To
                </label>
                <Autocomplete
                  fullWidth
                  disabled={!sourceWarehouse || isDraft}
                  options={allAvailableWarehouses.filter(
                    (wareHouse) => wareHouse.id !== sourceWarehouse?.id
                  )}
                  getOptionLabel={(option) => option.name}
                  value={destinationWarehouse}
                  onChange={(_, newValue) => {
                    setDestinationWarehouse(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: "0.875rem" },
                      }}
                    />
                  )}
                  ListboxProps={{
                    onScroll: handleScroll,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Items Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: "left",
              fontWeight: "600",
              color: "#1E1E1E",
              mb: 2,
              fontSize: "1.125rem",
            }}>
            Items
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                  <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      fontSize: "0.875rem",
                      width: "300px",
                    }}>
                    Product Description
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                    Quantity
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                    Unit Of Measure
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                    Unit Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                    Tax
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                    Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: "0.875rem" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        options={parts}
                        disabled
                        getOptionLabel={(option) => option.description}
                        value={item}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            disabled
                            variant="standard"
                            InputProps={{
                              ...params.InputProps,
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        )}
                        ListboxProps={{
                          onScroll: (event) => {
                            isAtBotton(event) &&
                              setPartsCurrentPageNumber(
                                partsCurrentPageNumber + 1
                              );
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.quantity}
                        variant="standard"
                        disabled
                        InputProps={{
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.quantity}
                        variant="standard"
                        disabled
                        InputProps={{
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.price}
                        variant="standard"
                        disabled
                        placeholder="Unit Price"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={(item.tax || 0) + " %"}
                        variant="standard"
                        disabled
                        InputProps={{
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={getTotalPriceOfPart(
                          item.quantity,
                          item.price,
                          item.tax
                        )}
                        placeholder="Total"
                        variant="standard"
                        disabled
                        InputProps={{
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        sx={{ color: "#FF0000" }}
                        size="small"
                        onClick={() => {
                          setItems((prev) => {
                            return prev.filter((_, idx) => index !== idx);
                          });
                        }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Add new Item */}
                <TableRow>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {/* New details */}
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      options={parts}
                      getOptionLabel={(part) => part.description}
                      onChange={(_, value) => {
                        if (value) {
                          setCurrentAddingPartDetails({
                            ...value,
                            quantity: 1,
                            tax: 18,
                          });
                        } else {
                          setCurrentAddingPartDetails(null);
                        }
                      }}
                      value={currentlyAddingPartDetails}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      )}
                      ListboxProps={{
                        onScroll: (event) => {
                          if (isAtBotton(event)) {
                            setPartsCurrentPageNumber(
                              partsCurrentPageNumber + 1
                            );
                          }
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={
                        currentlyAddingPartDetails
                          ? currentlyAddingPartDetails?.quantity || 1
                          : 0
                      }
                      variant="standard"
                      placeholder="Quantity"
                      type="number"
                      autoComplete="off"
                      InputProps={{
                        style: { fontSize: "0.875rem" },
                      }}
                      onChange={(newQuantity) => {
                        if (currentlyAddingPartDetails) {
                          setCurrentAddingPartDetails({
                            ...currentlyAddingPartDetails,
                            quantity: Math.max(
                              1,
                              parseInt(newQuantity.target.value, 10)
                            ),
                          });
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder="Unit of Measure"
                      value="Unit"
                      variant="standard"
                      disabled
                      InputProps={{
                        style: { fontSize: "0.875rem" },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                        style: { fontSize: "0.875rem" },
                      }}
                      placeholder="Unit Price"
                      disabled
                      value={currentlyAddingPartDetails?.price || ""}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder="Tax"
                      value={`${currentlyAddingPartDetails?.tax || 0} %`}
                      disabled
                      variant="standard"
                      InputProps={{
                        style: { fontSize: "0.875rem" },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder="Total"
                      variant="standard"
                      InputProps={{
                        style: { fontSize: "0.875rem" },
                      }}
                      disabled
                      value={
                        currentlyAddingPartDetails
                          ? getTotalPriceOfPart(
                              currentlyAddingPartDetails.quantity,
                              currentlyAddingPartDetails.price,
                              currentlyAddingPartDetails.tax
                            )
                          : ""
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      sx={{ color: "#1976D2" }}
                      size="small"
                      onClick={() => {
                        if (currentlyAddingPartDetails) {
                          setItems([...items, currentlyAddingPartDetails]);
                          setCurrentAddingPartDetails(null);
                        }
                      }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Totals and Actions Section */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ mb: 2, textAlign: "right" }}>
            <Typography
              variant="body1"
              sx={{ color: "#646464", fontSize: "0.875rem" }}>
              Amount - ₹ {totalWithoutTaxes}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#646464", fontSize: "0.875rem" }}>
              IGST - ₹ {totalGST}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#1E1E1E",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}>
              Total - ₹ {totalWithTaxes}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            {/* <Button
              variant="contained"
              color="error"
              sx={{
                padding: "8px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
                fontSize: "0.875rem",
                backgroundColor: "#F94932",
              }}>
              Cancel
            </Button> */}
            {isDraft && (
              <Button
                disabled={
                  !sourceWarehouse ||
                  !destinationWarehouse ||
                  items.length === 0
                }
                onClick={() => {
                  handleSubmit("DRAFT");
                }}
                variant="contained"
                color="primary"
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  backgroundColor: "#1366D9",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  "&:hover": { backgroundColor: "#1366D9" },
                }}>
                Save
              </Button>
            )}
            <Button
              disabled={
                !sourceWarehouse || !destinationWarehouse || items.length === 0
              }
              onClick={() => {
                handleSubmit("CREATED");
              }}
              variant="contained"
              color="primary"
              sx={{
                padding: "8px 20px",
                borderRadius: "32px",
                backgroundColor: "#1E1E1E",
                textTransform: "none",
                fontWeight: "500",
                fontSize: "0.875rem",
                "&:hover": { backgroundColor: "#000000" },
              }}>
              Create
            </Button>
          </Box>
        </Box>
      </Paper>
    </div>
  );
};

export default AppTransferOrderForm;
